import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { bmxurl, getAccess } from 'src/globals';
import { users_returndata } from 'src/types/user_returndata';
import { booking_returndata } from 'src/types/booking_returndata';
import { paymenttypes_returndata } from 'src/types/paymenttypes_returndata';
import { availabletimes_returndata } from 'src/types/availabletimes_returndata';
import { seriesvouchervalidate } from 'src/types/seriesvoucher';
import { seriesdiscountvalidate, seriesopendiscount } from 'src/types/seriesdiscount';
import { cBookingExtras } from 'src/types/booking';
import { AvailabilityReq, Payment } from 'src/PackageClasses';
import { admission } from 'src/types/admission';
import { GlobalService } from '../_services/global.service';
import { seriesbooking } from 'src/types/seriesbooking';
import { seriespayment as seriespaymentrequest } from 'src/types/seriespayment';
import { history } from 'src/types/history';
import { cRefund } from 'src/types/refund';
import { BusyService } from '../_services/busy.service';

//One payment, with enough of its booking alongside it to say which date it was taken against.
interface seriespayment {
  paymentDate: Date;
  method: string;
  amount: number;
  bookingIdHR: string;
  bookingStart: Date;
  //What a refund needs: the payment's id and how much of it is left to give back.
  paymentId: string;
  balance: number;
}

//One line of the series' own history.
interface serieshistoryrow {
  when: Date;
  text: string;
  user: string;
}

//What one date said when it was asked whether it could take a change.
interface datecheck {
  booking: any;
  ok: boolean;
  times: any[];
}

@Component({
  selector: 'app-editseries',
  templateUrl: './editseries.component.html',
  styleUrls: ['./editseries.component.css']
})
export class EditseriesComponent implements OnInit {

  VenueID: string = localStorage.getItem("SmartVenueID")!;
  //Stored settings come back as text, so an unset currency arrives as the word "null" rather than
  //nothing at all - which the currency pipe then prints in front of every figure.
  CurrencySymbol: string = EditseriesComponent.readCurrency();
  httpResponse: any;

  AccessAllowed: boolean = false;
  CompletedLoading: boolean = false;

  //The dates are loaded before anything is drawn, so "none found" is only shown once the looking
  //has actually finished.
  loaded: boolean = false;

  //Stops a second change being started while one is still running - everything here writes to
  //several bookings at once.
  saving: boolean = false;

  seriesid: string = "";

  //Carried through from the booking this screen was opened from, so going back lands on the day
  //the user was looking at rather than today.
  startdate: string = "";

  //Every date of the series, oldest first, as whole bookings.
  bookings: any[] = [];

  //The package all of the dates were booked on. Its admissions say what may be changed and within
  //what limits; its extras carry the questions.
  packageObj: any = null;

  activeIndex: number = 0;

  //Edited on its own rather than against one of the bookings, because it is written to all of them.
  customer: any = null;
  notes: string = "";

  firstNameValid: boolean = true;
  lastNameValid: boolean = true;
  emailValid: boolean = true;
  mobileValid: boolean = true;

  //The package's admissions, carrying the numbers the series is booked for.
  admissions: any[] = [];

  //The extras, with the quantity the series has of each.
  extras: any[] = [];

  //One row per date, holding the times that date's package entry offers and the one it is set to.
  //Times belong to a date rather than to the series: a package can run at 11:00 on one of its
  //dates and not on another, so the dates are not forced to share a time.
  timeRows: any[] = [];

  //What each date said last time it was asked, so a failure can name the dates.
  failedDates: any[] = [];

  paymentAmount: number = 0;
  PaymentTypes: any = [];
  paymentValueValid: boolean = true;

  showDiscount: boolean = false;
  openDiscount: boolean = false;
  discountCode: string = "";
  chosenDiscountAmount: number = 0;
  stateOptionOpenDiscount: any[] = [{ label: 'Enter Discount Value', value: true }, { label: 'Enter Discount Code', value: false }];

  showVoucher: boolean = false;
  voucherCode: string = "";
  voucherpin: string = "";

  //Refunds, as on a booking: only a Venue Administrator may give one.
  canRefund: boolean = false;
  showRefund: boolean = false;
  refundPaymentId: string = "";
  refundAmount: number = 0;
  refundMax: number = 0;
  refundReason: string = "";
  refundAmountValid: boolean = true;
  refundReasonValid: boolean = true;

  showCancel: boolean = false;
  cancelReason: string = "";

  //The API's own record of the series: the money across every date, every payment taken for the
  //series and its history. Null until fetched, and the figures fall back to the dates while it is.
  seriesRecord: seriesbooking = null;

  //History lines longer than this are cut short with a clickable ellipsis that opens the rest,
  //the way long package descriptions are. Rows are remembered by position in the list.
  readonly historyLimit: number = 150;
  expandedHistory = new Set<number>();

  toggleHistory(index: number)
  {
    if(this.expandedHistory.has(index))
    {
      this.expandedHistory.delete(index);
    }
    else
    {
      this.expandedHistory.add(index);
    }
  }

  constructor(
    private route: ActivatedRoute,
    private http: HttpClient,
    private router: Router,
    private messageService: MessageService,
    private globalService: GlobalService,
    private busyService: BusyService) {
  }

  ngOnInit(): void {

    this.globalService.setSmallScreenMenu(false);

    this.seriesid = this.route.snapshot.paramMap.get('seriesid');
    this.startdate = this.route.snapshot.paramMap.get('startdate');

    let userid = localStorage.getItem("SmartUserID");

    this.http.get(bmxurl + "User/" + userid).subscribe(response => {
      this.httpResponse = (response as users_returndata);

      if(this.httpResponse.succeeded)
      {
        let user = this.httpResponse.data;

        this.canRefund = user?.accesscontrol?.name == 'Venue Administrator';

        if(getAccess("MAKEABOOKING", user))
        {
          this.AccessAllowed = true;

          this.doPaymentTypes();
          this.loadSeries();
        }

        this.CompletedLoading = true;
      }
    }, error => {
      this.messageService.add({severity:'error', summary:'Failure', detail:'Failed to get User.'});
    });
  }

  //#region Loading

  //The list endpoint gives the dates in the series, but only as list rows. The whole booking is
  //needed for the people, the extras, the customer and the payments, so each one is then fetched
  //in full.
  loadSeries()
  {
    this.loadSeriesRecord();

    this.busyService.busy();

    this.http.get<any>(bmxurl + "BookingListByseriesid/" + this.VenueID + "/" + this.seriesid).subscribe(response => {
      const rows = (response?.data ?? []) as any[];

      if(rows.length == 0)
      {
        this.bookings = [];
        this.loaded = true;
        this.busyService.idle();
        return;
      }

      //A date that fails to load is left out rather than stopping the whole screen, so the rest of
      //the series can still be worked on.
      const calls = rows.map(row =>
        this.http.get<booking_returndata>(bmxurl + "booking/" + this.VenueID + "/" + row.bookingId).pipe(
          catchError(() => of(null))
        ));

      forkJoin(calls).subscribe(responses => {
        const loaded = (responses as any[])
          .filter(item => item != null && item.succeeded)
          .map(item => item.data);

        this.bookings = loaded.sort((first, second) => new Date(first.startTime).getTime() - new Date(second.startTime).getTime());

        if(this.bookings.length < rows.length)
        {
          this.messageService.add({severity:'warn', summary:'Not every date was loaded', detail:'Some dates of this series could not be read and are not shown. Nothing should be changed until that is sorted out.'});
        }

        this.readSharedDetails();
        this.loadPackage();

        this.loaded = true;
        this.busyService.idle();
      }, () => {
        this.loaded = true;
        this.busyService.idle();
        this.messageService.add({severity:'error', summary:'Failure', detail:'Failed to get the bookings in this series.'});
      });
    }, () => {
      this.loaded = true;
      this.busyService.idle();
      this.messageService.add({severity:'error', summary:'Failure', detail:'Failed to get this series.'});
    });
  }

  //GET /SeriesBooking. Read alongside the dates rather than before them, so a series the API has no
  //record for still opens - its figures are then added up from the dates.
  private loadSeriesRecord()
  {
    this.busyService.busy();

    this.http.get<any>(bmxurl + "SeriesBooking/" + this.VenueID + "/" + this.seriesid).subscribe(response => {
      this.busyService.idle();

      //Sent either wrapped like the other endpoints or as the record itself.
      const record: seriesbooking = response?.data !== undefined ? response.data : response;

      this.expandedHistory.clear();
      this.seriesRecord = (response?.succeeded === false || record == null || record.seriesid == null) ? null : record;

      //The amount box starts at what is owed, now that the series says what that is.
      if(this.seriesRecord != null && !this.saving)
      {
        this.paymentAmount = Math.max(0, this.seriesOutstanding);

        //The series keeps one set of notes for every date, so they are the ones shown.
        this.notes = this.seriesRecord.notes ?? "";
      }
    }, () => {
      this.busyService.idle();
      this.seriesRecord = null;
    });
  }

  //The customer, the notes and the extras are the same on every date, so the first date is what
  //gets shown. Copies are taken, so leaving the screen without saving changes nothing.
  private readSharedDetails()
  {
    if(this.bookings.length == 0)
    {
      return;
    }

    //The booking knows what it was sold in, which beats the venue's current setting.
    if(this.lead.currency)
    {
      this.CurrencySymbol = this.lead.currency;
    }

    this.customer = this.lead.customer == null ? null : JSON.parse(JSON.stringify(this.lead.customer));
    //The series' own notes if it has been read already - the dates can arrive after it does.
    this.notes = this.seriesRecord != null ? (this.seriesRecord.notes ?? "") : (this.lead.notes ?? "");
    this.extras = this.lead.extras == null ? [] : JSON.parse(JSON.stringify(this.lead.extras));
    this.paymentAmount = Math.max(0, this.seriesOutstanding);
  }

  //The package says which admissions exist and what their limits are; the booking says how many
  //people are on each. An admission the package has gained since the booking was made starts at
  //nobody.
  private loadPackage()
  {
    this.http.get<any>(bmxurl + "package/" + this.VenueID + "/" + this.lead.packageId).subscribe(response => {
      if(!response.succeeded)
      {
        this.messageService.add({severity:'error', summary:'Failure', detail:'Failed to get the package for this series.'});
        return;
      }

      this.packageObj = response.data;

      const booked = this.lead.resources != null && this.lead.resources.length > 0
        ? this.lead.resources[0].admissions
        : [];

      this.admissions = JSON.parse(JSON.stringify(this.packageObj.admissions ?? []));

      this.admissions.forEach(adm => {
        const already = (booked ?? []).find((item: any) => item.admissionid == adm.admissionid);
        adm.noofpeople = already == null ? 0 : already.noofpeople;
      });

      //An extra the booking has no record of still needs its question and its limits.
      this.mergeExtraDetails();

      //The times each date may run at come from the package, so they can only be worked out once
      //the package is here.
      this.buildTimeRows();
    }, () => {
      this.messageService.add({severity:'error', summary:'Failure', detail:'Failed to get the package for this series.'});
    });
  }

  private mergeExtraDetails()
  {
    const master = this.packageObj?.extras ?? [];

    if(this.extras.length == 0)
    {
      this.extras = JSON.parse(JSON.stringify(master));
      this.extras.forEach(extra => extra.qty = 0);
      return;
    }

    this.extras.forEach(extra => {
      const found = master.find((item: any) =>
        item.id === extra.id || item.packageExtraId === extra.packageExtraId || item.extraId === extra.extraId);

      if(found != null)
      {
        extra.question = extra.question == null || extra.question == "" ? found.question : extra.question;
        extra.min = found.min;
        extra.max = found.max;
      }
    });
  }

  doPaymentTypes()
  {
    const cached = localStorage.getItem("PaymentTypes");

    if(cached)
    {
      this.PaymentTypes = JSON.parse(cached).filter(pt => pt.system == false);
      return;
    }

    this.http.get(bmxurl + "paymenttypes").subscribe(response => {
      this.PaymentTypes = (response as paymenttypes_returndata).data;

      localStorage.setItem("PaymentTypes", JSON.stringify(this.PaymentTypes));

      this.PaymentTypes = this.PaymentTypes.filter(pt => pt.system == false);
    }, () => {
      this.messageService.add({severity:'error', summary:'Failure', detail:'Failed to get Payment Types.'});
    });
  }

  //#endregion

  //#region What the screen shows

  get lead(): any
  {
    return this.bookings[0];
  }

  get customerName(): string
  {
    const person = this.lead?.customer;

    if(person == null)
    {
      return "no customer";
    }

    return (person.fullName ?? "").trim() != "" ? person.fullName : (person.firstName + " " + person.lastName).trim();
  }

  get firstDate(): Date
  {
    return this.lead?.startTime;
  }

  get lastDate(): Date
  {
    return this.bookings[this.bookings.length - 1]?.startTime;
  }

  //The series record is where the money across every date is kept, so its figures win; adding up
  //the dates is only the fallback for when it can't be had.
  get seriesTotal(): number
  {
    if(this.seriesRecord != null)
    {
      return this.roundMoney(this.seriesRecord.total);
    }

    return this.roundMoney(this.bookings.reduce((running, item) => running + (Number(item.total) || 0), 0));
  }

  get seriesOutstanding(): number
  {
    if(this.seriesRecord != null)
    {
      return this.roundMoney(this.seriesRecord.outstanding);
    }

    return this.roundMoney(this.bookings.reduce((running, item) => running + (Number(item.outstanding) || 0), 0));
  }

  get seriesPaid(): number
  {
    if(this.seriesRecord != null)
    {
      return this.roundMoney(this.seriesRecord.totalpayments);
    }

    return this.roundMoney(this.seriesTotal - this.seriesOutstanding);
  }

  get seriesRefunded(): number
  {
    return this.roundMoney(this.seriesRecord?.totalrefunds);
  }

  //The series' history, newest first.
  get seriesHistory(): serieshistoryrow[]
  {
    return (this.seriesRecord?.history ?? [])
      .map((item: history) => ({
        when: item.itemdatetime,
        text: item.historyitem,
        user: item.user?.name ?? (item.system ? "System" : "")
      }))
      .sort((first, second) => new Date(second.when).getTime() - new Date(first.when).getTime());
  }

  get totalPeople(): number
  {
    return this.admissions.reduce((running, item) => running + (Number(item.noofpeople) || 0), 0);
  }

  get allDisabled(): boolean
  {
    return this.bookings.length > 0 && this.bookings.every(item => item.disabled);
  }

  peopleOn(booking: any): number
  {
    if(booking.resources == null || booking.resources.length == 0)
    {
      return 0;
    }

    return (booking.resources[0].admissions ?? []).reduce((running: number, item: any) => running + (Number(item.noofpeople) || 0), 0);
  }

  //Every payment on every date, newest first, each saying which date it belongs to.
  get allPayments(): seriespayment[]
  {
    let rows: seriespayment[] = [];

    //Payments for the series are taken against the series, not a date, so its record lists them.
    if((this.seriesRecord?.payments ?? []).length > 0)
    {
      this.seriesRecord.payments.forEach((payment: any) => {
        rows.push({
          paymentDate: payment.paymentDate ?? payment.PaymentDate,
          method: payment.method ?? payment.Method,
          amount: payment.amount ?? payment.Amount,
          bookingIdHR: payment.bookingIdHR ?? payment.BookingIdHR,
          bookingStart: null,
          paymentId: payment.paymentId ?? payment.PaymentId ?? payment.id,
          balance: Number(payment.balance) || 0
        });
      });

      return rows.sort((first, second) => new Date(second.paymentDate).getTime() - new Date(first.paymentDate).getTime());
    }

    this.bookings.forEach(booking => {
      (booking.payments ?? []).forEach((payment: any) => {
        rows.push({
          paymentDate: payment.paymentDate,
          method: payment.method,
          amount: payment.amount,
          bookingIdHR: booking.bookingIdHR,
          bookingStart: booking.startTime,
          paymentId: payment.paymentId ?? payment.PaymentId ?? payment.id,
          balance: Number(payment.balance) || 0
        });
      });
    });

    return rows.sort((first, second) => new Date(second.paymentDate).getTime() - new Date(first.paymentDate).getTime());
  }

  private roundMoney(value: number): number
  {
    return Math.round((Number(value) || 0) * 100) / 100;
  }

  //The time of day as it is stored, which is read as written rather than shifted into local time.
  private timeOf(when: any): string
  {
    const value = new Date(when);
    return ('00' + value.getUTCHours()).slice(-2) + ":" + ('00' + value.getUTCMinutes()).slice(-2);
  }

  //The day a booking runs on, written the way the package's repeat dates are.
  private dayOf(when: any): string
  {
    const value = new Date(when);

    return value.getUTCFullYear() + "-" + ('00' + (value.getUTCMonth() + 1)).slice(-2) + "-" + ('00' + value.getUTCDate()).slice(-2);
  }

  //The times this package runs at on that booking's day. A repeatable package ignores Start Times
  //entirely - it runs only at the times listed in its own repeat dates, and one day can carry
  //several of them.
  private packageTimesFor(booking: any): string[]
  {
    const day = this.dayOf(booking.startTime);

    return (this.packageObj?.repeatdates ?? [])
      .filter((entry: string) => entry.substring(0, 10) == day)
      .map((entry: string) => entry.substring(11, 16));
  }

  viewBooking(bookingid: string)
  {
    this.router.navigateByUrl("/editbooking/" + bookingid);
  }

  //Goes back to wherever the user actually came from. The screens that open a booking leave a note
  //of themselves behind, and this reads the same notes, in the same order, as Edit Booking does -
  //otherwise someone who came from the home page is dropped on the bookings list instead.
  returnToList()
  {
    if(localStorage.getItem("voucherScreen") == "TRUE")
    {
      localStorage.setItem("voucherScreen", "FALSE");
      this.router.navigateByUrl("/voucherlist");
    }
    else if(localStorage.getItem("ataglance") == "TRUE")
    {
      localStorage.setItem("ataglance", "FALSE");
      let AAGData = localStorage.getItem("AAGData");
      localStorage.setItem("AAGData", "");
      this.router.navigateByUrl("/bookingblock/" + AAGData);
    }
    else if(localStorage.getItem("listScreen") == "TRUE")
    {
      localStorage.setItem("listScreen", "FALSE");
      this.router.navigateByUrl("/bookinglist");
    }
    else if(localStorage.getItem("counterBookingList") != null && localStorage.getItem("counterBookingList") != "")
    {
      let counterType = localStorage.getItem("counterBookingList");
      localStorage.removeItem("counterBookingList");
      this.router.navigateByUrl("/counterbookinglist/" + counterType);
    }
    else if(localStorage.getItem("aagBookingList") != null && localStorage.getItem("aagBookingList") != "")
    {
      let aagDate = localStorage.getItem("aagBookingList");
      localStorage.removeItem("aagBookingList");
      this.router.navigateByUrl("/ataglancebookings/" + aagDate);
    }
    else if(localStorage.getItem("homeitem") == "TRUE")
    {
      localStorage.setItem("homeitem", "FALSE");
      this.router.navigateByUrl("/homepagechoice");
    }
    else if(this.startdate != null && this.startdate != "")
    {
      this.router.navigateByUrl("/home/" + this.startdate);
    }
    else
    {
      //No day to go back to, so the home page as it opens itself.
      this.router.navigateByUrl("/");
    }
  }

  //#endregion

  //#region Asking every date whether it can take a change

  //Asks one date what it could do with these admissions. The date's own booking is ignored while
  //the question is asked, so the room it already holds counts as free.
  private availabilityFor(booking: any, admissions: any[], answer: (times: any[]) => void)
  {
    const when = new Date(booking.startTime);

    const request = {} as AvailabilityReq;
    request.Admissions = [] as admission[];

    let people = 0;

    admissions.forEach(adm => {
      request.Admissions.push(adm);
      people += Number(adm.noofpeople) || 0;
    });

    request.packageId = booking.packageId;
    request.NoOfPeople = people;
    request.venueid = this.VenueID;
    request.StartTime = when.getUTCFullYear() + "-" + (when.getUTCMonth() + 1) + "-" + when.getUTCDate() + "T00:00:00.000Z";
    request.DiagnosticMode = false;
    request.IgnoreExistingBooking = true;
    request.ExistingBookingID = booking.bookingId;
    request.Resources = booking.resources;

    this.http.post<availabletimes_returndata>(bmxurl + "/Availability", request).subscribe(response => {
      answer((response.data ?? []) as any[]);
    }, () => {
      answer([]);
    });
  }

  //Asks every date at once, then hands back what each one said.
  private availabilityForAll(admissions: any[], answer: (checks: datecheck[]) => void)
  {
    let waiting = this.bookings.length;
    let checks: datecheck[] = this.bookings.map(booking => ({ booking: booking, ok: false, times: [] }));

    this.bookings.forEach((booking, index) => {
      this.availabilityFor(booking, admissions, times => {
        checks[index].times = times;
        waiting--;

        if(waiting == 0)
        {
          answer(checks);
        }
      });
    });
  }

  //#endregion

  //#region Changing the number of people

  //Every date is asked first. If a single date cannot take the new numbers nothing is saved at
  //all, so the series never ends up with different numbers on different dates.
  savePeopleToAll()
  {
    if(this.saving || this.bookings.length == 0)
    {
      return;
    }

    if(this.totalPeople <= 0)
    {
      this.messageService.add({severity:'error', summary:'Nobody on the booking', detail:'The series needs at least one person on it.'});
      return;
    }

    this.saving = true;
    this.failedDates = [];
    this.busyService.busy();

    this.availabilityForAll(this.admissions, checks => {

      //Only the time this date already runs at matters here - the numbers are changing, not the
      //time. The slot carries its own time as text, which is what the package's times are compared
      //against everywhere else.
      checks.forEach(check => {
        const wanted = this.timeOf(check.booking.startTime);
        check.ok = check.times.some(time => time.timeAvailable && time.timeString == wanted);
      });

      const failed = checks.filter(check => !check.ok);

      if(failed.length > 0)
      {
        this.failedDates = failed.map(check => check.booking);
        this.saving = false;
        this.busyService.idle();

        this.messageService.add({
          severity:'error',
          summary:'Not enough room',
          detail: failed.length + (failed.length == 1 ? ' date cannot' : ' dates cannot') + ' take ' + this.totalPeople + (this.totalPeople == 1 ? ' person' : ' people') + '. Nothing has been changed - the dates are listed on the page.'});

        return;
      }

      //The checking is over. Saving goes busy again on its own, and the spinner counts up and down
      //rather than simply being on or off - so without this the count never gets back to nothing
      //and the spinner turns for the rest of the session.
      this.busyService.idle();

      //Every date can take it, so now it is saved everywhere.
      this.applyToEveryDate(booking => {
        if(booking.resources != null && booking.resources.length > 0)
        {
          booking.resources.forEach((res: any) => {
            res.admissions = JSON.parse(JSON.stringify(this.admissions));
          });
        }
      }, 'The number of people was changed on every date.');
    });
  }

  //#endregion

  //#region Changing the time of day

  //One row per date. A date may only run at the times the package sets against that date, and
  //those differ from date to date - so each date is offered its own times rather than the dates
  //being made to share one.
  private buildTimeRows()
  {
    this.timeRows = this.bookings.map(booking => {
      const current = this.timeOf(booking.startTime);
      let options = this.packageTimesFor(booking);

      //The time the date actually runs at is always offered, even where the package no longer
      //lists it - otherwise the box would come up empty on a date whose package has been edited
      //underneath it.
      if(options.indexOf(current) == -1)
      {
        options = [...options, current];
      }

      return {
        booking: booking,
        options: options.sort(),
        chosen: current,
        current: current
      };
    });
  }

  get changedTimeCount(): number
  {
    return this.timeRows.filter(row => row.chosen != row.current).length;
  }

  //Only the dates whose time was actually changed are checked and moved; the rest are left as
  //they are. If any one of the changed dates can't take its new time, none of them are moved.
  saveTimes()
  {
    if(this.saving || this.bookings.length == 0)
    {
      return;
    }

    if(this.changedTimeCount == 0)
    {
      this.messageService.add({severity:'info', summary:'No change', detail:'No date has been given a different time.'});
      return;
    }

    this.saving = true;
    this.failedDates = [];
    this.busyService.busy();

    this.availabilityForAll(this.admissions, checks => {

      //The slot carries the resources that go with it, so the one each date is moving to has to be
      //found before anything is saved.
      let slots: any[] = [];

      checks.forEach((check, index) => {
        const row = this.timeRows[index];

        if(row == null || row.chosen == row.current)
        {
          //Not moving, so nothing to find and nothing that can fail.
          check.ok = true;
          slots.push(null);
          return;
        }

        const slot = check.times.find(time => time.timeAvailable && time.timeString == row.chosen);

        check.ok = slot != null;
        slots.push(slot);
      });

      const failed = checks.filter(check => !check.ok);

      if(failed.length > 0)
      {
        this.failedDates = failed.map(check => check.booking);
        this.saving = false;
        this.busyService.idle();

        this.messageService.add({
          severity:'error',
          summary:'Time not free',
          detail: failed.length + (failed.length == 1 ? ' date cannot' : ' dates cannot') + ' take the time chosen for it. Nothing has been changed - the dates are listed on the page.'});

        return;
      }

      const moving = this.changedTimeCount;

      //The checking is over, and saving goes busy on its own account.
      this.busyService.idle();

      this.applyToEveryDate((booking, index) => {
        if(slots[index] != null)
        {
          booking.startTime = slots[index].time;
          booking.resources = slots[index].resources;
        }
      }, moving + (moving == 1 ? ' date was' : ' dates were') + ' given a new time.');
    });
  }

  //#endregion

  //#region Extras

  //Extras are the same on every date, so the list is saved against each booking in turn.
  saveExtrasToAll()
  {
    if(this.saving || this.bookings.length == 0)
    {
      return;
    }

    //An extra that asks a question has to have an answer before it can be sold.
    const unanswered = this.extras.some(extra =>
      extra.qty > 0 && extra.question != null && extra.question != "" && (extra.answer == null || extra.answer == ""));

    if(unanswered)
    {
      this.messageService.add({severity:'error', summary:'Question not answered', detail:'Answer the question on every extra being sold.'});
      return;
    }

    this.extras.forEach(extra => {
      if(extra.qty == 0)
      {
        extra.answer = "";
      }
    });

    this.saving = true;
    this.busyService.busy();

    let waiting = this.bookings.length;
    let failed = 0;

    this.bookings.forEach(booking => {

      let be: cBookingExtras = {
        BookingId: booking.bookingId,
        venueid: this.VenueID,
        extras: JSON.parse(JSON.stringify(this.extras))
      };

      this.http.post<any>(bmxurl + "/BookingExtra", be).subscribe(response => {
        if(!response.succeeded)
        {
          failed++;
        }

        waiting--;

        if(waiting == 0)
        {
          this.afterWritingEveryDate(failed, 'The extras were saved to every date.');
        }
      }, () => {
        failed++;
        waiting--;

        if(waiting == 0)
        {
          this.afterWritingEveryDate(failed, 'The extras were saved to every date.');
        }
      });
    });
  }

  //#endregion

  //#region Saving the details every date shares

  saveCustomerToAll()
  {
    this.firstNameValid = (this.customer.firstName ?? "").trim() != "";
    this.lastNameValid = (this.customer.lastName ?? "").trim() != "";
    this.emailValid = this.validEmail(this.customer.email ?? "");
    this.mobileValid = (this.customer.mobile ?? "").trim() != "";

    if(!this.firstNameValid || !this.lastNameValid || !this.emailValid || !this.mobileValid)
    {
      return;
    }

    //Each date keeps its own customer record - only what was edited is copied onto it, so the ids
    //tying a customer to its booking are left alone.
    this.applyToEveryDate(booking => {
      if(booking.customer == null)
      {
        return;
      }

      booking.customer.firstName = this.customer.firstName;
      booking.customer.lastName = this.customer.lastName;
      booking.customer.fullName = (this.customer.firstName + " " + this.customer.lastName).trim();
      booking.customer.email = this.customer.email;
      booking.customer.mobile = this.customer.mobile;
      booking.customer.address1 = this.customer.address1;
      booking.customer.address2 = this.customer.address2;
      booking.customer.townCity = this.customer.townCity;
      booking.customer.postCode = this.customer.postCode;
    }, "The customer was saved to every date.");
  }

  //The series keeps one set of notes for every date, so they are saved onto the series with
  //PUT /SeriesBooking rather than onto each date.
  saveNotesToAll()
  {
    if(this.saving)
    {
      return;
    }

    if(this.seriesRecord == null)
    {
      this.messageService.add({severity:'error', summary:'Not saved', detail:'The series could not be read, so its notes could not be saved. Reload the page and try again.'});
      return;
    }

    const updated: seriesbooking = { ...this.seriesRecord, notes: this.notes };

    this.saving = true;
    this.busyService.busy();

    this.http.put<any>(bmxurl + "SeriesBooking", updated).subscribe(response => {
      this.saving = false;
      this.busyService.idle();

      if(response?.succeeded === false)
      {
        this.messageService.add({severity:'error', summary:'Not saved', detail: response.messages?.[0] ?? 'The notes could not be saved.'});
        return;
      }

      this.messageService.add({severity:'success', summary:'Saved', detail:'The notes were saved to the series.'});
      this.loadSeriesRecord();
    }, error => {
      this.saving = false;
      this.busyService.idle();
      this.messageService.add({severity:'error', summary:'Not saved', detail: error.message});
    });
  }

  //Applies a change to every date and saves them all. Each date is a booking in its own right, so
  //there is a save for each one; the screen is reloaded afterwards so what is shown is what the
  //API actually stored, including any re-pricing it did.
  private applyToEveryDate(change: (booking: any, index: number) => void, successMessage: string)
  {
    if(this.bookings.length == 0)
    {
      return;
    }

    this.saving = true;
    this.busyService.busy();

    let waiting = this.bookings.length;
    let failed = 0;

    this.bookings.forEach((booking, index) => {
      change(booking, index);

      this.http.put<any>(bmxurl + "/savebooking/true", booking).subscribe(response => {
        if(!response.succeeded)
        {
          failed++;
        }

        waiting--;

        if(waiting == 0)
        {
          this.afterWritingEveryDate(failed, successMessage);
        }
      }, () => {
        failed++;
        waiting--;

        if(waiting == 0)
        {
          this.afterWritingEveryDate(failed, successMessage);
        }
      });
    });
  }

  private afterWritingEveryDate(failed: number, successMessage: string)
  {
    this.saving = false;
    this.busyService.idle();

    if(failed > 0)
    {
      this.messageService.add({severity:'error', summary:'Not every date was saved', detail: failed + (failed == 1 ? ' date' : ' dates') + ' could not be saved, so the series no longer matches. Check the dates before changing anything else.'});
    }
    else
    {
      this.messageService.add({severity:'success', summary:'Saved', detail: successMessage});
    }

    this.loadSeries();
  }

  //#endregion

  //#region Cancelling the series

  cancelSeries()
  {
    if(this.saving || this.bookings.length == 0)
    {
      return;
    }

    if((this.cancelReason ?? "").trim() == "")
    {
      this.messageService.add({severity:'error', summary:'No reason given', detail:'Say why the series is being cancelled.'});
      return;
    }

    this.saving = true;
    this.busyService.busy();

    let waiting = this.bookings.length;
    let failed = 0;

    this.bookings.forEach(booking => {
      this.http.delete<any>(bmxurl + "Booking/" + this.VenueID + "/" + booking.bookingId).subscribe(() => {

        //The reason is kept on each booking's own history, the same way a single booking records it.
        let historyEntry: any = {
          bookingid: booking.bookingId,
          historyitem: "This Booking was disabled for the following reason: " + this.cancelReason + " (the whole series was cancelled)"
        };

        this.http.post<any>(bmxurl + "/History", historyEntry).subscribe(() => {}, () => {});

        waiting--;

        if(waiting == 0)
        {
          this.afterCancelling(failed);
        }
      }, () => {
        failed++;
        waiting--;

        if(waiting == 0)
        {
          this.afterCancelling(failed);
        }
      });
    });
  }

  private afterCancelling(failed: number)
  {
    this.saving = false;
    this.showCancel = false;
    this.cancelReason = "";
    this.busyService.idle();

    if(failed > 0)
    {
      this.messageService.add({severity:'error', summary:'Not every date was cancelled', detail: failed + (failed == 1 ? ' date is' : ' dates are') + ' still live. Check the dates.'});
    }
    else
    {
      this.messageService.add({severity:'success', summary:'Series cancelled', detail:'Every date in this series was cancelled.'});
    }

    this.loadSeries();
  }

  //#endregion

  //#region Money

  //One payment for the whole series through /SeriesPayment, which shares it across the dates and
  //completes them itself, so nothing is sent to the bookings here. Made out to the series. Each
  //payment type has its own button, as on a booking's payment section.
  takeSeriesPayment(paymenttypeid: string, method: string)
  {
    this.paymentValueValid = this.paymentAmount != null && this.paymentAmount > 0;

    if(!this.paymentValueValid || this.saving)
    {
      return;
    }

    const newPayment: Payment = {
      id: null,
      PaymentId: null,
      BookingId: null,
      bookingIdHR: (this.seriesRecord as any)?.seriesidhr ?? this.lead?.seriesidhr ?? null,
      method: method,
      paymenttypeid: paymenttypeid,
      amount: this.paymentAmount,
      paymentDate: new Date,
      disabled: false,
      venueid: this.VenueID,
      refunds: null,
      balance: this.paymentAmount,
      paymenttype: "booking",
      currency: this.CurrencySymbol,
      tillname: null,
      seriesid: this.seriesid,
      isseriespayment: true
    };

    const request: seriespaymentrequest = {
      seriesid: this.seriesid,
      payment: newPayment
    };

    this.saving = true;
    this.busyService.busy();

    this.http.post<booking_returndata>(bmxurl + "/SeriesPayment", request).subscribe(response => {
      this.saving = false;
      this.busyService.idle();

      if(response.succeeded != true)
      {
        this.messageService.add({severity:'error', summary:'Payment not taken', detail: (response as any).messages?.[0] ?? 'The payment could not be taken.'});
        return;
      }

      this.messageService.add({severity:'success', summary:'Payment taken', detail:'The payment was taken for the whole series.'});
      this.loadSeries();
    }, error => {
      this.saving = false;
      this.busyService.idle();
      this.messageService.add({severity:'error', summary:'Payment not taken', detail: error.message});
    });
  }

  //#region Refunds
  //POST /refund, the same one a booking's payments use. The amount starts at what is left of the
  //payment and cannot go past it.

  setupRefund(payment: seriespayment)
  {
    if(!this.canRefund)
    {
      this.messageService.add({severity:'error', summary:'Not Allowed', detail: 'You do not have authority to refund this payment, please speak to your manager.'});
      return;
    }

    this.refundPaymentId = payment.paymentId;
    this.refundMax = this.roundMoney(payment.balance);
    this.refundAmount = this.refundMax;
    this.refundReason = "";
    this.refundAmountValid = true;
    this.refundReasonValid = true;
    this.showRefund = true;
  }

  doRefund()
  {
    this.refundAmountValid = this.refundAmount != null && this.refundAmount > 0 && this.refundAmount <= this.refundMax;
    this.refundReasonValid = (this.refundReason ?? "").trim() != "";

    if(!this.refundAmountValid || !this.refundReasonValid || this.saving)
    {
      return;
    }

    const newRefund: cRefund = {
      venueid: this.VenueID,
      paymentid: this.refundPaymentId,
      reason: this.refundReason.trim(),
      amount: this.refundAmount,
      notes: "",
      disabled: false
    };

    this.saving = true;
    this.busyService.busy();

    this.http.post<any>(bmxurl + "/refund", newRefund).subscribe(response => {
      this.saving = false;
      this.busyService.idle();

      if(!response?.succeeded)
      {
        this.messageService.add({severity:'error', summary:'Not Saved', detail: response?.messages?.[0] ?? 'The refund could not be made.'});
        return;
      }

      this.showRefund = false;
      this.messageService.add({severity:'success', summary:'Saved', detail:'Refund successful, the refund has been passed to the card issuer and will be processed by them in accordance to their refund policy.'});
      this.loadSeries();
    }, error => {
      this.saving = false;
      this.busyService.idle();
      this.messageService.add({severity:'error', summary:'Not Saved', detail: error.message});
    });
  }

  //#endregion

  //#region Discounts
  //A discount is put on the series as a whole - POST /SeriesDiscountValidate for a code and
  //POST /SeriesOpenDiscount for an amount - and the series record then lists it.

  get seriesDiscounts(): any[]
  {
    return this.seriesRecord?.discounts ?? [];
  }

  applySeriesDiscountCode()
  {
    const code = (this.discountCode ?? "").trim();

    if(this.saving || code == "")
    {
      this.messageService.add({severity:'error', summary:'No discount', detail:'Enter a discount code.'});
      return;
    }

    const request: seriesdiscountvalidate = {
      discountcode: code,
      venueid: this.VenueID,
      seriesid: this.seriesid
    };

    this.sendSeriesDiscount("/SeriesDiscountValidate", request);
  }

  applySeriesOpenDiscount()
  {
    const value = this.roundMoney(this.chosenDiscountAmount);

    if(this.saving || value <= 0)
    {
      this.messageService.add({severity:'error', summary:'No discount', detail:'Enter a discount amount.'});
      return;
    }

    const request: seriesopendiscount = {
      value: value,
      venueid: this.VenueID,
      seriesid: this.seriesid
    };

    this.sendSeriesDiscount("/SeriesOpenDiscount", request);
  }

  private sendSeriesDiscount(endpoint: string, request: any)
  {
    this.saving = true;
    this.busyService.busy();

    this.http.post<any>(bmxurl + endpoint, request).subscribe(response => {
      this.saving = false;
      this.busyService.idle();

      if(response?.succeeded !== true)
      {
        this.messageService.add({severity:'error', summary:'Not Added', detail: response?.messages?.[0] ?? 'The discount could not be added.'});
        return;
      }

      this.messageService.add({severity:'success', summary:'Discount Added', detail:'Your discount has been added.'});
      this.showDiscount = false;
      this.discountCode = "";
      this.chosenDiscountAmount = 0;
      this.loadSeries();
    }, error => {
      this.saving = false;
      this.busyService.idle();
      this.messageService.add({severity:'error', summary:'Not Added', detail: error?.message});
    });
  }

  //#endregion

  //POST /SeriesVoucherValidate puts the voucher against the series as a whole.
  applyVoucher()
  {
    const pin = Number((this.voucherpin ?? "").toString().trim());

    if(this.saving || (this.voucherCode ?? "").trim() == "" || isNaN(pin))
    {
      this.messageService.add({severity:'error', summary:'No voucher', detail:'Enter the gift voucher code and its PIN.'});
      return;
    }

    const request: seriesvouchervalidate = {
      vouchercode: this.voucherCode.trim(),
      voucherpin: pin,
      venueid: this.VenueID,
      seriesid: this.seriesid
    };

    this.saving = true;
    this.busyService.busy();

    this.http.post<any>(bmxurl + "/SeriesVoucherValidate", request).subscribe(response => {
      this.saving = false;
      this.busyService.idle();

      if(response?.succeeded !== true)
      {
        this.messageService.add({severity:'error', summary:'Not Added', detail: response?.messages?.[0] ?? 'The voucher could not be used.'});
        return;
      }

      this.messageService.add({severity:'success', summary:'Voucher Added', detail:'Your voucher has been added.'});
      this.voucherCode = "";
      this.voucherpin = "";
      this.showVoucher = false;
      this.loadSeries();
    }, error => {
      this.saving = false;
      this.busyService.idle();
      this.messageService.add({severity:'error', summary:'Not Added', detail: error?.error?.messages?.[0] ?? error?.message});
    });
  }

  //#endregion

  //"null" and "undefined" are what an unset setting actually looks like once it has been through
  //localStorage, and an empty string is no use to the currency pipe either.
  private static readCurrency(): string
  {
    const stored = localStorage.getItem("Currency");

    if(stored == null || stored == "" || stored == "null" || stored == "undefined")
    {
      return "GBP";
    }

    return stored;
  }

  private validEmail(email: string): boolean
  {
    const pattern = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    return pattern.test(email);
  }
}
